import { ref, get } from "firebase/database";
import { db } from "../auth/firebase";
import { getWriterPlanFeatures } from "../config/writerPlans";
import { CONTENT_STATUS, CONTENT_VISIBILITY } from "../features/content/contentConstants";
import { isSuperAdmin } from "../utils/roleUtils";

export function writerUsageMonthKeyUtc(d = new Date()) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export async function getWriterMonthlyPublishedCount(uid) {
  if (!uid) return 0;
  const key = writerUsageMonthKeyUtc();
  const snap = await get(ref(db, `users/${uid}/usage/writerMonthlyPublished/${key}`));
  return Number(snap.val() || 0);
}

function isPublishedItem(item) {
  return item && String(item.status || "").toLowerCase() === CONTENT_STATUS.PUBLISHED;
}

/**
 * Client-side guard before publish (server still enforces).
 * @returns {{ ok: boolean, reason?: string }}
 */
export async function evaluateWriterPublishTransition({ user, previousItem, nextPayload }) {
  if (isSuperAdmin(user)) return { ok: true };
  if (!user?.subscriptions?.writer && String(user?.role || "").toLowerCase() !== "writer") {
    return { ok: true };
  }

  const feats = getWriterPlanFeatures(user);
  const nextPub = String(nextPayload.status || "").toLowerCase() === CONTENT_STATUS.PUBLISHED;
  const prevPub = isPublishedItem(previousItem);

  if (!nextPub) return { ok: true };

  const vis = String(nextPayload.visibility || "").toLowerCase();
  if (!feats.allowPrivateVisibility && vis === CONTENT_VISIBILITY.PRIVATE) {
    return { ok: false, reason: "Private visibility requires Basic or Pro." };
  }
  if (!feats.allowPremiumVisibility && vis === CONTENT_VISIBILITY.PREMIUM) {
    return { ok: false, reason: "Premium visibility requires Basic or Pro." };
  }
  if (feats.planId === "free" && vis !== CONTENT_VISIBILITY.PUBLIC) {
    return { ok: false, reason: "Free plan allows only public published content." };
  }

  if (prevPub) return { ok: true };

  const limit = feats.maxPublishedPerMonth;
  if (limit === Number.POSITIVE_INFINITY) return { ok: true };

  const used = await getWriterMonthlyPublishedCount(user.uid);
  if (used >= limit) {
    return {
      ok: false,
      reason: `Monthly publish limit reached (${limit} per calendar month, UTC). Upgrade your writer plan to publish more.`,
    };
  }
  return { ok: true };
}
